// src/Views/NotificationsBell.js
// 🔥 ALL notifications expandable
// 🔥 FIXED: Added backdrop overlay to prevent Support page bleed-through

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import './NotificationsBell.css';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import {
    collection,
    query,
    orderBy,
    limit,
    onSnapshot,
    doc,
    updateDoc,
    where,
    getDocs,
    addDoc,
    serverTimestamp
} from 'firebase/firestore';

const NotificationsBell = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const btnRef = useRef(null);
    const panelRef = useRef(null);

    const [open, setOpen] = useState(false);
    const [expandedId, setExpandedId] = useState(null);
    
    const [systemItems, setSystemItems] = useState([]);
    const [personalItems, setPersonalItems] = useState([]);
    const [isPremium, setIsPremium] = useState(false);
    const [loading, setLoading] = useState(true);
    const [pos, setPos] = useState({ top: 0, left: 0, width: 360 });

    // Listen to User Profile
    useEffect(() => {
        if (!user) return;
        const unsub = onSnapshot(doc(db, 'users', user.uid), (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                const premium = data.isPremium === true || (data.premiumUntil && data.premiumUntil.toMillis() > Date.now());
                setIsPremium(!!premium);
            }
        });
        return () => unsub();
    }, [user]);

    // Listen to Global Announcements
    useEffect(() => {
        const q = query(
            collection(db, 'announcements'),
            orderBy('createdAt', 'desc'),
            limit(10)
        );
        const unsub = onSnapshot(q, (snap) => {
            const data = snap.docs.map((docSnap) => ({
                id: docSnap.id,
                source: 'system',
                ...docSnap.data()
            }));
            setSystemItems(data);
            setLoading(false);
        });
        return () => unsub();
    }, []);

    // Listen to Personal Notifications
    useEffect(() => {
        if (!user) {
            setPersonalItems([]);
            return;
        }
        const q = query(
            collection(db, 'users', user.uid, 'notifications'),
            orderBy('createdAt', 'desc'),
            limit(10)
        );
        const unsub = onSnapshot(q, (snap) => {
            const data = snap.docs.map((docSnap) => ({
                id: docSnap.id,
                source: 'personal',
                ...docSnap.data()
            }));
            setPersonalItems(data);
        });

        // Self-Healing Welcome Message
        const checkAndCreateWelcome = async () => {
            try {
                const notifRef = collection(db, 'users', user.uid, 'notifications');
                const welcomeQ = query(notifRef, where('type', '==', 'success'), limit(1));
                const snap = await getDocs(welcomeQ);
                if (snap.empty) {
                    await addDoc(notifRef, {
                        title: "Welcome to AiPredict! 🚀",
                        message: "Your account is setup. Before you start, please read the Risk Strategy guide in the Premium Dashboard.",
                        type: "success",
                        read: false,
                        link: "/premium/how-to-play",
                        createdAt: serverTimestamp()
                    });
                }
            } catch (err) {
                console.error("Self-check failed:", err);
            }
        };
        checkAndCreateWelcome();
        return () => unsub();
    }, [user]);

    // Merge & Filter
    const allItems = useMemo(() => {
        const filteredSystem = systemItems.filter(item => {
            if (item.targetAudience === 'premium' && !isPremium) return false;
            return true;
        });
        const combined = [...personalItems, ...filteredSystem];
        const sorted = combined.sort((a, b) => {
            const tA = a.createdAt?.toMillis() || 0;
            const tB = b.createdAt?.toMillis() || 0;
            return tB - tA;
        });
        return sorted.map(item => {
            if (item.type === 'crypto') {
                return { ...item, title: 'New Crypto Signal', body: 'New high-probability setup detected. Tap to unlock.' };
            }
            return item;
        });
    }, [personalItems, systemItems, isPremium]);

    const hasUnread = useMemo(() => personalItems.some(i => !i.read), [personalItems]);
    const showDot = hasUnread || (systemItems.length > 0 && !localStorage.getItem('saw_system'));

    const computePos = useCallback(() => {
        const el = btnRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const panelWidth = 380;
        let left = rect.right - panelWidth;
        if (left < 10) left = 10;
        setPos({ top: rect.bottom + 12, left, width: panelWidth });
    }, []);

    const handleToggle = useCallback(() => {
        setOpen(prev => {
            const next = !prev;
            if (next) {
                requestAnimationFrame(computePos);
                localStorage.setItem('saw_system', 'true');
            } else {
                setExpandedId(null);
            }
            return next;
        });
    }, [computePos]);

    // 🔥 TOGGLE EXPAND - for all notification types
    const toggleExpand = useCallback(async (item) => {
        // Mark as read if personal notification
        if (item.source === 'personal' && !item.read && user) {
            try {
                const ref = doc(db, 'users', user.uid, 'notifications', item.id);
                await updateDoc(ref, { read: true });
            } catch (err) { console.error(err); }
        }

        // Toggle expand state
        setExpandedId(prev => prev === item.id ? null : item.id);
    }, [user]);

    // 🔥 NAVIGATE - for crypto/football/items with links
    const handleNavigate = useCallback((item) => {
        setOpen(false);
        setExpandedId(null);

        if (item.type === 'crypto') {
            navigate('/crypto');
        } else if (item.type === 'football') {
            navigate('/football');
        } else if (item.link || item.linkPath) {
            navigate(item.link || item.linkPath);
        }
    }, [navigate]);

    // 🔥 Check if item should show navigation button
    const hasNavigation = (item) => {
        return item.type === 'crypto' || item.type === 'football' || item.link || item.linkPath;
    };

    // 🔥 Close panel handler
    const handleClose = useCallback(() => {
        setOpen(false);
        setExpandedId(null);
    }, []);

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        
        const handleOutside = (e) => {
            // 🔥 iOS FIX: Ignore taps on action buttons
            if (e.target.closest('.notif__action-btn')) return;
            if (e.target.closest('.notif__item-actions')) return;
            if (panelRef.current?.contains(e.target)) return;
            if (btnRef.current?.contains(e.target)) return;
            // 🔥 Allow clicking overlay to close
            if (e.target.closest('.notif__overlay')) {
                handleClose();
                return;
            }
            handleClose();
        };
        
        // 🔥 iOS: Use touchend instead of touchstart to avoid conflicts
        document.addEventListener('mousedown', handleOutside);
        document.addEventListener('touchend', handleOutside, { passive: true });
        window.addEventListener('resize', computePos);
        window.addEventListener('scroll', computePos, true);
        
        return () => {
            document.removeEventListener('mousedown', handleOutside);
            document.removeEventListener('touchend', handleOutside);
            window.removeEventListener('resize', computePos);
            window.removeEventListener('scroll', computePos, true);
        };
    }, [open, computePos, handleClose]);

    // Helpers
    const getIcon = (type) => {
        if (type === 'crypto') return 'solar:chart-2-bold';
        if (type === 'football') return 'solar:football-bold';
        if (type === 'success') return 'solar:check-circle-bold';
        if (type === 'warning') return 'solar:danger-triangle-bold';
        return 'solar:bell-bing-bold';
    };

    const getIconClass = (type) => {
        if (type === 'crypto') return 'notif__icon-box--crypto';
        if (type === 'football') return 'notif__icon-box--football';
        if (type === 'success') return 'notif__icon-box--success';
        return 'notif__icon-box--default';
    };

    const getFullMessage = (item) => item.message || item.body || item.preview || '';
    
    const getPreview = (item) => {
        const full = getFullMessage(item);
        return full.length <= 80 ? full : full.substring(0, 80) + '...';
    };
    
    const isExpandable = (item) => getFullMessage(item).length > 80;

    const formatTime = (timestamp) => {
        if (!timestamp) return 'Recent';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        const diff = Date.now() - date;
        if (diff < 60000) return 'Just now';
        if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
        if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
        if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
        return date.toLocaleDateString();
    };

    // Get navigation label based on type
    const getNavLabel = (item) => {
        if (item.type === 'crypto') return 'View Crypto';
        if (item.type === 'football') return 'View Football';
        return 'View Details';
    };

    // Render Panel
    const panelNode = open ? (
        <>
            {/* 🔥 NEW: Backdrop overlay to cover page content */}
            <div 
                className="notif__overlay" 
                onClick={handleClose}
                onTouchEnd={(e) => {
                    e.preventDefault();
                    handleClose();
                }}
            />
            
            {/* Panel */}
            <div
                ref={panelRef}
                className="notif__panel"
            >
                <div className="notif__header">
                    <span className="notif__header-title">Notifications</span>
                    {hasUnread && <span className="notif__header-badge">New</span>}
                </div>

                <div className="notif__list">
                    {!loading && allItems.length === 0 && (
                        <div className="notif__empty">
                            <Icon icon="solar:bell-off-linear" className="notif__empty-icon" />
                            <p>No active signals.</p>
                        </div>
                    )}

                    {allItems.map((item) => {
                        const isExpanded = expandedId === item.id;
                        const canExpand = isExpandable(item);
                        const canNavigate = hasNavigation(item);
                        
                        return (
                            <div
                                key={item.id}
                                className={`notif__item ${item.source === 'personal' && !item.read ? 'notif__item--unread' : ''} ${isExpanded ? 'notif__item--expanded' : ''}`}
                            >
                                {/* Main Row */}
                                <div className="notif__item-main">
                                    <div className={`notif__icon-box ${getIconClass(item.type)}`}>
                                        <Icon icon={getIcon(item.type)} />
                                    </div>
                                    
                                    <div className="notif__content">
                                        <div className="notif__row-top">
                                            <span className="notif__tag">
                                                {item.source === 'personal' ? 'ALERT' : (item.type || 'INFO').toUpperCase()}
                                                {item.targetAudience === 'premium' && <span style={{color:'#f59e0b', marginLeft:'4px'}}>★</span>}
                                            </span>
                                            <span className="notif__time">{formatTime(item.createdAt)}</span>
                                        </div>
                                        <p className="notif__title">{item.title}</p>
                                        
                                        {/* Message - truncated or full based on expand state */}
                                        <p className={`notif__body ${isExpanded ? 'notif__body--expanded' : ''}`}>
                                            {isExpanded ? getFullMessage(item) : getPreview(item)}
                                        </p>
                                    </div>
                                    
                                    {item.source === 'personal' && !item.read && (
                                        <div className="notif__unread-dot" />
                                    )}
                                </div>
                                
                                {/* 🔥 ACTION ROW - Always visible */}
                                <div className="notif__item-actions">
                                    {/* Expand/Collapse Button - only if message is long */}
                                    {canExpand && (
                                        <button
                                            type="button"
                                            className="notif__action-btn notif__action-btn--expand"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                toggleExpand(item);
                                            }}
                                        >
                                            <Icon icon={isExpanded ? "solar:alt-arrow-up-linear" : "solar:alt-arrow-down-linear"} />
                                            {isExpanded ? 'Show less' : 'Read more'}
                                        </button>
                                    )}
                                    
                                    {/* Navigation Button - only for crypto/football/items with links */}
                                    {canNavigate && (
                                        <button
                                            type="button"
                                            className="notif__action-btn notif__action-btn--nav"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleNavigate(item);
                                            }}
                                        >
                                            <Icon icon="solar:arrow-right-linear" />
                                            {getNavLabel(item)}
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
                
                <div className="notif__footer">
                    <button onClick={handleClose}>Close Feed</button>
                </div>
            </div>
        </>
    ) : null;

    return (
        <div className="notif">
            <button
                ref={btnRef}
                type="button"
                className={`notif__btn ${open ? 'notif__btn--active' : ''}`}
                onClick={handleToggle}
            >
                <Icon icon="solar:bell-bing-bold" className="notif__bell-icon" />
                {showDot && <span className="notif__dot" />}
            </button>
            
            {/* 🔥 FIX: Use portal to render at body level - fixes iOS stacking issues */}
            {open && createPortal(panelNode, document.body)}
        </div>
    );
};

export default NotificationsBell;